import React from "react";

/**
 * On the philosophy:
 * duplicated orders should behave like normal creation forms with one exception:
 * the first render must preserve copied partner-scoped values instead of letting creation-time
 * autofill logic immediately replace them with supplier/customer defaults.
 *
 * This context keeps that concern local to the mutation layout subtree, so copied-draft behavior
 * is explicit, temporary, and isolated from the persisted form data itself.
 */
// Copy hydration state is kept outside RHF because only a few creation-only sections need to know
// whether they are rendering the first pass of a duplicated draft.
type CopyHydrationContextValue = {
    isCopiedDraft: boolean;
    copiedEntityId?: number;
    copiedEntityType: "supplier" | "customer" | null;
};

const defaultContextValue: CopyHydrationContextValue = {
    isCopiedDraft: false,
    copiedEntityType: null,
};

const CopyHydrationContext = React.createContext<CopyHydrationContextValue>(defaultContextValue);

export function CopyHydrationProvider({
    children,
    value,
}: React.PropsWithChildren<{ value: CopyHydrationContextValue }>) {
    return <CopyHydrationContext.Provider value={value}>{children}</CopyHydrationContext.Provider>;
}

export function useCopyHydrationContext() {
    return React.useContext(CopyHydrationContext);
}

export function useShouldSkipCopyAutofill(entityId?: number) {
    const { copiedEntityId, isCopiedDraft } = useCopyHydrationContext();
    // Once the user leaves the copied supplier/customer, the normal creation autofill flow should
    // resume and stay enabled for the rest of the form session.
    const hasLeftCopiedEntityRef = React.useRef(false);

    React.useEffect(() => {
        if (!isCopiedDraft || copiedEntityId === undefined) {
            hasLeftCopiedEntityRef.current = false;
            return;
        }

        if (entityId !== undefined && entityId !== copiedEntityId) {
            hasLeftCopiedEntityRef.current = true;
        }
    }, [copiedEntityId, entityId, isCopiedDraft]);

    return (
        isCopiedDraft &&
        !hasLeftCopiedEntityRef.current &&
        copiedEntityId !== undefined &&
        entityId === copiedEntityId
    );
}
